import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/mutations"

import {
  CancelTransferDocument,
  type CancelTransferFailure,
  type CancelTransferMutation,
  type CancelTransferMutationVariables,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseCancelTransferReturn {
  cancelTransfer: (transferId: string) => Promise<boolean>
  isCancelling: boolean
}

export const useCancelTransfer = (): UseCancelTransferReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<CancelTransferMutation, CancelTransferMutationVariables>(
    CancelTransferDocument
  )

  const cancelTransfer = useCallback(
    async (transferId: string): Promise<boolean> => {
      const { data, error } = await executeMutation({ input: { transferId } })
      const output = data?.transfer.cancelTransfer
      const hasError = error || output?.__typename === "CancelTransferFailure"

      if (hasError) {
        const message = error?.message ?? (output as CancelTransferFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Transfer cancelled" })
      return true
    },
    [executeMutation, notify]
  )

  return { cancelTransfer, isCancelling: fetching }
}
